import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import { 
  User, MapPin, Calendar, Shield, BadgeCheck, Package, 
  MessageSquare, Building2, ArrowLeft, Home, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ListingCard from '../components/ListingCard';
import { API } from '../App';

const SellerProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSellerProfile();
  }, [userId]);

  const fetchSellerProfile = async () => {
    try {
      const response = await axios.get(`${API}/sellers/${userId}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch seller profile:', error);
      toast.error('Failed to load seller profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 max-w-6xl py-16">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-4 max-w-6xl py-16 text-center">
          <User className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Seller not found</h1>
          <p className="text-muted-foreground">This seller profile doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const { seller, stats, listings } = profile;
  const memberYear = new Date(seller.created_at).getFullYear();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 max-w-6xl py-8">
        {/* Navigation Bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          
          {/* Breadcrumbs - Desktop only / Back button - Mobile only */}
          <div className="flex items-center gap-2">
            {/* Mobile: Only back button */}
            <button
              onClick={() => navigate(-1)}
              className="sm:hidden flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            
            {/* Desktop: Full breadcrumbs */}
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to="/"
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Home className="w-4 h-4" />
                Home
              </Link>
              <span className="text-muted-foreground">/</span>
              <Link
                to="/marketplace"
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                Marketplace
              </Link>
              <span className="text-muted-foreground">/</span>
              <span className="text-sm text-foreground font-medium truncate max-w-[150px] md:max-w-none">
                {seller.business_name || seller.name}
              </span>
            </div>
          </div>
        </div>

        {/* Seller Header */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden mb-8">
          {/* Banner - Custom or gradient fallback */}
          {seller.banner_url ? (
            <div className="h-32 md:h-40 overflow-hidden">
              <img 
                src={seller.banner_url} 
                alt={`${seller.business_name || seller.name} banner`} 
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="bg-gradient-to-r from-primary/20 to-teal-500/20 h-24" />
          )}
          <div className="px-6 pb-6 -mt-12">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              {/* Avatar */}
              <div className="w-24 h-24 rounded-2xl border-4 border-card bg-card flex items-center justify-center overflow-hidden shadow-lg">
                {seller.avatar ? (
                  <img src={seller.avatar} alt={seller.name} className="w-full h-full object-cover" />
                ) : seller.business_logo ? (
                  <img src={seller.business_logo} alt={seller.business_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary to-teal-500 flex items-center justify-center text-white text-3xl font-bold">
                    {(seller.business_name || seller.name || 'S')[0].toUpperCase()}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h1 className="text-2xl font-bold text-foreground" data-testid="seller-name">
                    {seller.business_name || seller.name}
                  </h1>
                  {seller.account_type === 'business' && (
                    <span className="px-2 py-0.5 bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-medium rounded-full flex items-center gap-1 border border-orange-500/20">
                      <Building2 className="w-3 h-3" />
                      Business
                    </span>
                  )}
                  {seller.account_type === 'business' && seller.is_verified_business && (
                    <span className="px-2 py-0.5 bg-green-500/10 text-green-600 dark:text-green-400 text-xs font-medium rounded-full flex items-center gap-1 border border-green-500/20">
                      <BadgeCheck className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                  {seller.usdt_erc20_address && (
                    <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium rounded-full flex items-center gap-1">
                      <svg className="w-3 h-3" viewBox="0 0 32 32" fill="currentColor">
                        <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0zm7.994 18.394c-.093.387-.307.68-.64.88-.333.2-.74.3-1.22.3H17.6v4.36h-3.2v-4.36H9.866c-.48 0-.887-.1-1.22-.3-.333-.2-.547-.493-.64-.88V17.1h13.988v1.294zm0-3.2H8.006v-1.28c.093-.4.307-.7.64-.9.333-.2.74-.3 1.22-.3H14.4V8.34h3.2v4.374h4.534c.48 0 .887.1 1.22.3.333.2.547.5.64.9v1.28z"/>
                      </svg>
                      Accepts USDT
                    </span>
                  )}
                  {seller.website && (
                    <a 
                      href={seller.website.startsWith('http') ? seller.website : `https://${seller.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-0.5 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-full flex items-center gap-1 hover:bg-blue-500/20 transition-colors"
                    >
                      <Globe className="w-3 h-3" />
                      Website
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  {seller.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" /> {seller.location}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> Seller since {memberYear}
                  </span>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex gap-6 mt-4 md:mt-0">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{stats.total_listings}</div>
                  <div className="text-xs text-muted-foreground">Listings</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">{stats.response_rate}%</div>
                  <div className="text-xs text-muted-foreground">Response</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Listings Section */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Listings ({stats.total_listings})
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map((listing) => (
            <ListingCard key={listing.id} listing={listing} />
          ))}
          
          {listings.length === 0 && (
            <div className="col-span-full text-center py-12">
              <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No active listings</p>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SellerProfilePage;
